import { NO, UIView, YES } from "uicore-ts"
import { CBButtonGroup } from "../CB/CBButtonGroup"
import { RHButton, RHButtonVariant } from "./RHButton"
import { RHButtons, RHSpacing } from "./Utils_slop/RHDesignTokens"
import { RHSegmentedTabBarViewTokens } from "./Utils_slop/RHViewTokens"


export interface RHSegmentedTabDefinition {
    key: string
    title: string
    unselectedVariant?: RHButtonVariant
    selectedVariant?: RHButtonVariant
}


/**
 * RHSegmentedTabBarView — the shared segmented tab row used across RH screens
 * (e.g. the procurement-details "Üldandmed / Pakkumused / …" tabs and the
 * procurer "Töölaud" "Avaldatud / Ettevalmistamisel / Uus" tabs). It owns one
 * toggleable RHButton per tab, a CBButtonGroup that enforces single-selection,
 * and the centered fixed-width row layout. The owner reacts to user selection
 * via `selectionDidChange` and reflects external state (e.g. a route tab
 * parameter) back into the bar via the `selectedKey` setter.
 */
export class RHSegmentedTabBarView extends UIView {
    
    readonly tabButtons: RHButton[]
    readonly buttonGroup: CBButtonGroup
    
    private readonly _tabKeys: string[]
    private readonly _unselectedVariantOverrides: (RHButtonVariant | undefined)[]
    private readonly _selectedVariantOverrides: (RHButtonVariant | undefined)[]
    private _suppressSelectionCallback: boolean = NO
    
    // Invoked only on user-initiated tab changes, never when `selectedKey` is set
    // programmatically to mirror external state.
    selectionDidChange?: (key: string) => void
    
    
    constructor(tabDefinitions: RHSegmentedTabDefinition[], elementID?: string) {
        super(elementID)
        
        this._tabKeys = tabDefinitions.map(tabDefinition => tabDefinition.key)
        this._unselectedVariantOverrides = tabDefinitions.map(tabDefinition => tabDefinition.unselectedVariant)
        this._selectedVariantOverrides = tabDefinitions.map(tabDefinition => tabDefinition.selectedVariant)
        
        this.tabButtons = tabDefinitions.map(tabDefinition =>
            new RHButton().configuredWithObject({
                titleLabel: { text: tabDefinition.title },
                variant: RHSegmentedTabBarViewTokens.unselectedVariant,
                isToggleable: YES,
                contentPadding: RHButtons.compactContentPadding,
                style: {
                    fontSize: RHButtons.compactFontSize
                }
            }).addedAsSubviewToView(this)
        )
        
        const groupConfiguration: Record<string, RHButton> = {}
        tabDefinitions.forEach((tabDefinition, index) => {
            groupConfiguration[tabDefinition.key] = this.tabButtons[index]
        })
        
        this.buttonGroup = new CBButtonGroup(groupConfiguration).configuredWithObject({
            value: this._tabKeys.firstElement,
            selectionDidChange: (_selectedButton, value) => {
                this._applyVariantsForSelectedKey(value as string)
                if (!this._suppressSelectionCallback) {
                    this.selectionDidChange?.(value as string)
                }
            }
        })
        
        this._applyVariantsForSelectedKey(this._tabKeys.firstElement)
    }


    tabButtonForKey(key: string): RHButton | undefined {
        return this.buttonGroup.buttonsMap[key] as RHButton | undefined
    }
    
    
    get selectedKey(): string {
        return this.buttonGroup.value as string
    }
    
    set selectedKey(key: string) {
        if (this.buttonGroup.value === key) {
            this._applyVariantsForSelectedKey(key)
            return
        }
        this._suppressSelectionCallback = YES
        this.buttonGroup.value = key
        this._suppressSelectionCallback = NO
        this._applyVariantsForSelectedKey(key)
    }
    
    
    private _applyVariantsForSelectedKey(selectedKey: string) {
        this.tabButtons.forEach((tabButton, index) => {
            if (this._tabKeys[index] === selectedKey) {
                tabButton.variant = this._selectedVariantOverrides[index] ?? "primary"
            }
            else {
                tabButton.variant = this._unselectedVariantOverrides[index] ?? RHSegmentedTabBarViewTokens.unselectedVariant
            }
        })
    }
    
    
    // ── Layout ──────────────────────────────────────────────────────────────
    
    override intrinsicContentHeight(_constrainingWidth: number = 0): number {
        return RHButtons.compactHeight
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const bounds = this.contentBoundsWithInset(0)
        const visibleTabButtons = this.tabButtons.filter(tabButton => !tabButton.hidden)
        
        const preferredWidths = visibleTabButtons.map(() => 210)
        const preferredRowWidth =
            preferredWidths.reduce((total, width) => total + width, 0) +
            RHSpacing.medium * Math.max(0, visibleTabButtons.length - 1)
        
        if (preferredRowWidth <= bounds.width) {
            bounds.framesByDistributingViewsAsRow(
                visibleTabButtons,
                RHSpacing.medium,
                preferredWidths,
                0.5
            )
        }
        else {
            bounds.distributeViewsEquallyAlongWidth(
                visibleTabButtons,
                RHSpacing.medium
            )
        }
    }
    
}
